import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  NotificationMetrics,
  NotificationTypeMetrics,
  NotificationTrendData,
  UserEngagementScore,
  NotificationVolumeByTime,
  NotificationVolumeByDay,
  DateRange,
  TrendGranularity,
  DashboardStats,
} from '../types/notification-admin';
import { NotificationType } from '../types/user';

/**
 * NotificationAnalyticsService
 * Provides analytics and metrics for the notification system
 */
export class NotificationAnalyticsService {
  private static readonly COLLECTION_NAME = 'notifications';

  /**
   * Get overall notification metrics for a date range
   */
  static async getOverallMetrics(dateRange?: DateRange): Promise<NotificationMetrics> {
    try {
      const constraints: QueryConstraint[] = [];

      if (dateRange) {
        constraints.push(where('createdAt', '>=', Timestamp.fromDate(dateRange.start)));
        constraints.push(where('createdAt', '<=', Timestamp.fromDate(dateRange.end)));
      }

      const q = query(collection(db, this.COLLECTION_NAME), ...constraints);
      const snapshot = await getDocs(q);

      let totalSent = 0;
      let totalDelivered = 0;
      let totalRead = 0;
      let totalActioned = 0;
      let totalReadTime = 0;
      let readTimeCount = 0;

      snapshot.forEach((doc) => {
        const data = doc.data();
        totalSent++;

        // Count delivered (not failed)
        if (!data.deliveryError) {
          totalDelivered++;
        }

        // Count read
        if (data.read) {
          totalRead++;

          // Calculate time to read
          if (data.createdAt && data.readAt) {
            const createdTime = data.createdAt.toDate().getTime();
            const readTime = data.readAt.toDate().getTime();
            const minutesToRead = (readTime - createdTime) / (1000 * 60);
            totalReadTime += minutesToRead;
            readTimeCount++;
          }
        }

        // Count actioned (if has actionUrl and was clicked)
        if (data.actionUrl && data.clickedAt) {
          totalActioned++;
        }
      });

      const deliveryRate = totalSent > 0 ? (totalDelivered / totalSent) * 100 : 0;
      const readRate = totalDelivered > 0 ? (totalRead / totalDelivered) * 100 : 0;
      const actionRate = totalRead > 0 ? (totalActioned / totalRead) * 100 : 0;
      const avgTimeToRead = readTimeCount > 0 ? totalReadTime / readTimeCount : 0;

      return {
        totalSent,
        totalDelivered,
        totalRead,
        totalActioned,
        deliveryRate,
        readRate,
        actionRate,
        avgTimeToRead,
      };
    } catch (error: any) {
      console.error('Error getting overall metrics:', error);
      throw error;
    }
  }

  /**
   * Get metrics broken down by notification type
   */
  static async getMetricsByType(dateRange?: DateRange): Promise<NotificationTypeMetrics[]> {
    try {
      const constraints: QueryConstraint[] = [];

      if (dateRange) {
        constraints.push(where('createdAt', '>=', Timestamp.fromDate(dateRange.start)));
        constraints.push(where('createdAt', '<=', Timestamp.fromDate(dateRange.end)));
      }

      const q = query(collection(db, this.COLLECTION_NAME), ...constraints);
      const snapshot = await getDocs(q);

      // Group by type
      const typeMap = new Map<NotificationType, {
        sent: number;
        delivered: number;
        read: number;
        actioned: number;
        totalReadTime: number;
        readTimeCount: number;
      }>();

      snapshot.forEach((doc) => {
        const data = doc.data();
        const type = data.type as NotificationType;

        if (!typeMap.has(type)) {
          typeMap.set(type, {
            sent: 0,
            delivered: 0,
            read: 0,
            actioned: 0,
            totalReadTime: 0,
            readTimeCount: 0,
          });
        }

        const stats = typeMap.get(type)!;
        stats.sent++;

        if (!data.deliveryError) {
          stats.delivered++;
        }

        if (data.read) {
          stats.read++;

          if (data.createdAt && data.readAt) {
            const createdTime = data.createdAt.toDate().getTime();
            const readTime = data.readAt.toDate().getTime();
            const minutesToRead = (readTime - createdTime) / (1000 * 60);
            stats.totalReadTime += minutesToRead;
            stats.readTimeCount++;
          }
        }

        if (data.actionUrl && data.clickedAt) {
          stats.actioned++;
        }
      });

      // Convert to array of metrics
      const metrics: NotificationTypeMetrics[] = [];
      typeMap.forEach((stats, type) => {
        const deliveryRate = stats.sent > 0 ? (stats.delivered / stats.sent) * 100 : 0;
        const readRate = stats.delivered > 0 ? (stats.read / stats.delivered) * 100 : 0;
        const actionRate = stats.read > 0 ? (stats.actioned / stats.read) * 100 : 0;
        const avgTimeToRead = stats.readTimeCount > 0 ? stats.totalReadTime / stats.readTimeCount : 0;

        metrics.push({
          type,
          typeLabel: this.formatTypeLabel(type),
          totalSent: stats.sent,
          totalDelivered: stats.delivered,
          totalRead: stats.read,
          totalActioned: stats.actioned,
          deliveryRate,
          readRate,
          actionRate,
          avgTimeToRead,
        });
      });

      // Sort by total sent (descending)
      metrics.sort((a, b) => b.totalSent - a.totalSent);

      return metrics;
    } catch (error: any) {
      console.error('Error getting metrics by type:', error);
      throw error;
    }
  }

  /**
   * Get trend data over time
   */
  static async getTrendData(
    dateRange: DateRange,
    granularity: TrendGranularity = 'day'
  ): Promise<NotificationTrendData[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
        where('createdAt', '<=', Timestamp.fromDate(dateRange.end)),
        orderBy('createdAt', 'asc')
      );

      const snapshot = await getDocs(q);

      // Group by time period
      const timeMap = new Map<string, {
        date: Date;
        sent: number;
        delivered: number;
        read: number;
        actioned: number;
      }>();

      snapshot.forEach((doc) => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate();
        if (!createdAt) return;

        const timeKey = this.getTimeKey(createdAt, granularity);

        if (!timeMap.has(timeKey)) {
          timeMap.set(timeKey, {
            date: this.getTimePeriodStart(createdAt, granularity),
            sent: 0,
            delivered: 0,
            read: 0,
            actioned: 0,
          });
        }

        const stats = timeMap.get(timeKey)!;
        stats.sent++;

        if (!data.deliveryError) {
          stats.delivered++;
        }

        if (data.read) {
          stats.read++;
        }

        if (data.actionUrl && data.clickedAt) {
          stats.actioned++;
        }
      });

      // Convert to array and sort by date
      const trendData: NotificationTrendData[] = Array.from(timeMap.values())
        .sort((a, b) => a.date.getTime() - b.date.getTime());

      return trendData;
    } catch (error: any) {
      console.error('Error getting trend data:', error);
      throw error;
    }
  }

  /**
   * Get user engagement scores
   */
  static async getUserEngagementScores(limit: number = 50): Promise<UserEngagementScore[]> {
    try {
      const q = query(collection(db, this.COLLECTION_NAME));
      const snapshot = await getDocs(q);

      // Group by user
      const userMap = new Map<string, {
        userId: string;
        userName?: string;
        userEmail?: string;
        totalReceived: number;
        totalRead: number;
        totalActioned: number;
        totalReadTime: number;
        readTimeCount: number;
        lastInteractionAt?: Date;
      }>();

      snapshot.forEach((doc) => {
        const data = doc.data();
        const userId = data.userId;

        if (!userMap.has(userId)) {
          userMap.set(userId, {
            userId,
            userName: data.userName,
            userEmail: data.userEmail,
            totalReceived: 0,
            totalRead: 0,
            totalActioned: 0,
            totalReadTime: 0,
            readTimeCount: 0,
          });
        }

        const stats = userMap.get(userId)!;
        stats.totalReceived++;

        if (data.read) {
          stats.totalRead++;

          const readAt = data.readAt?.toDate();
          if (readAt && (!stats.lastInteractionAt || readAt > stats.lastInteractionAt)) {
            stats.lastInteractionAt = readAt;
          }

          if (data.createdAt && data.readAt) {
            const createdTime = data.createdAt.toDate().getTime();
            const readTime = data.readAt.toDate().getTime();
            const minutesToRead = (readTime - createdTime) / (1000 * 60);
            stats.totalReadTime += minutesToRead;
            stats.readTimeCount++;
          }
        }

        if (data.actionUrl && data.clickedAt) {
          stats.totalActioned++;

          const clickedAt = data.clickedAt.toDate();
          if (!stats.lastInteractionAt || clickedAt > stats.lastInteractionAt) {
            stats.lastInteractionAt = clickedAt;
          }
        }
      });

      // Convert to array and calculate rates
      const scores: UserEngagementScore[] = [];
      userMap.forEach((stats) => {
        const readRate = stats.totalReceived > 0 ? (stats.totalRead / stats.totalReceived) * 100 : 0;
        const actionRate = stats.totalRead > 0 ? (stats.totalActioned / stats.totalRead) * 100 : 0;
        const avgTimeToRead = stats.readTimeCount > 0 ? stats.totalReadTime / stats.readTimeCount : 0;

        scores.push({
          userId: stats.userId,
          userName: stats.userName || 'Unknown',
          userEmail: stats.userEmail || '',
          totalReceived: stats.totalReceived,
          totalRead: stats.totalRead,
          totalActioned: stats.totalActioned,
          readRate,
          actionRate,
          avgTimeToRead,
          lastInteractionAt: stats.lastInteractionAt,
        });
      });

      // Sort by engagement (read rate * action rate)
      scores.sort((a, b) => {
        const scoreA = a.readRate * a.actionRate;
        const scoreB = b.readRate * b.actionRate;
        return scoreB - scoreA;
      });

      return scores.slice(0, limit);
    } catch (error: any) {
      console.error('Error getting user engagement scores:', error);
      throw error;
    }
  }

  /**
   * Get notification volume by hour of day
   */
  static async getVolumeByTimeOfDay(): Promise<NotificationVolumeByTime[]> {
    try {
      const q = query(collection(db, this.COLLECTION_NAME));
      const snapshot = await getDocs(q);

      const hourCounts = new Array(24).fill(0);

      snapshot.forEach((doc) => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate();
        if (createdAt) {
          const hour = createdAt.getHours();
          hourCounts[hour]++;
        }
      });

      return hourCounts.map((count, hour) => ({ hour, count }));
    } catch (error: any) {
      console.error('Error getting volume by time of day:', error);
      throw error;
    }
  }

  /**
   * Get notification volume by day of week
   */
  static async getVolumeByDayOfWeek(): Promise<NotificationVolumeByDay[]> {
    try {
      const q = query(collection(db, this.COLLECTION_NAME));
      const snapshot = await getDocs(q);

      const dayCounts = new Array(7).fill(0);
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

      snapshot.forEach((doc) => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate();
        if (createdAt) {
          const day = createdAt.getDay();
          dayCounts[day]++;
        }
      });

      return dayCounts.map((count, day) => ({
        day,
        dayName: dayNames[day],
        count,
      }));
    } catch (error: any) {
      console.error('Error getting volume by day of week:', error);
      throw error;
    }
  }

  /**
   * Get dashboard statistics
   */
  static async getDashboardStats(): Promise<DashboardStats> {
    try {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const lastWeek = new Date(today);
      lastWeek.setDate(lastWeek.getDate() - 7);
      const lastMonth = new Date(today);
      lastMonth.setMonth(lastMonth.getMonth() - 1);

      // Get today's notifications
      const todayMetrics = await this.getOverallMetrics({
        start: today,
        end: now,
      });

      // Get yesterday's notifications
      const yesterdayMetrics = await this.getOverallMetrics({
        start: yesterday,
        end: today,
      });

      // Get last week's notifications
      const weekMetrics = await this.getOverallMetrics({
        start: lastWeek,
        end: now,
      });

      // Get last month's notifications
      const monthMetrics = await this.getOverallMetrics({
        start: lastMonth,
        end: now,
      });

      // Calculate trends
      const dailyTrend = yesterdayMetrics.totalSent > 0
        ? ((todayMetrics.totalSent - yesterdayMetrics.totalSent) / yesterdayMetrics.totalSent) * 100
        : 0;

      const weeklyTrend = 0; // Would need week-over-week comparison
      const monthlyTrend = 0; // Would need month-over-month comparison

      // Get active types count
      const typeMetrics = await this.getMetricsByType({ start: lastMonth, end: now });
      const activeTypes = typeMetrics.length;

      // Get total unread
      const unreadQuery = query(
        collection(db, this.COLLECTION_NAME),
        where('read', '==', false),
        where('archived', '==', false)
      );
      const unreadSnapshot = await getDocs(unreadQuery);
      const totalUnread = unreadSnapshot.size;

      return {
        todaySent: todayMetrics.totalSent,
        todayRead: todayMetrics.totalRead,
        todayUnread: todayMetrics.totalSent - todayMetrics.totalRead,
        todayReadRate: todayMetrics.readRate,
        yesterdaySent: yesterdayMetrics.totalSent,
        weekSent: weekMetrics.totalSent,
        monthSent: monthMetrics.totalSent,
        dailyTrend,
        weeklyTrend,
        monthlyTrend,
        activeTypes,
        totalUnread,
        pendingDeliveries: 0, // Would need delivery queue
        deliverySuccessRate: todayMetrics.deliveryRate,
        avgDeliveryTime: 0, // Would need delivery timestamps
        failedDeliveries: todayMetrics.totalSent - todayMetrics.totalDelivered,
      };
    } catch (error: any) {
      console.error('Error getting dashboard stats:', error);
      throw error;
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Format notification type as human-readable label
   */
  private static formatTypeLabel(type: NotificationType): string {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Get time key for grouping by granularity
   */
  private static getTimeKey(date: Date, granularity: TrendGranularity): string {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const hour = date.getHours();

    switch (granularity) {
      case 'hour':
        return `${year}-${month}-${day}-${hour}`;
      case 'day':
        return `${year}-${month}-${day}`;
      case 'week':
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        return `${weekStart.getFullYear()}-${weekStart.getMonth()}-${weekStart.getDate()}`;
      case 'month':
        return `${year}-${month}`;
      default:
        return `${year}-${month}-${day}`;
    }
  }

  /**
   * Get start of time period for granularity
   */
  private static getTimePeriodStart(date: Date, granularity: TrendGranularity): Date {
    const result = new Date(date);

    switch (granularity) {
      case 'hour':
        result.setMinutes(0, 0, 0);
        break;
      case 'day':
        result.setHours(0, 0, 0, 0);
        break;
      case 'week':
        result.setDate(date.getDate() - date.getDay());
        result.setHours(0, 0, 0, 0);
        break;
      case 'month':
        result.setDate(1);
        result.setHours(0, 0, 0, 0);
        break;
    }

    return result;
  }
}
